/**
 * Interactive resize handles for artwork scaling
 * Corner handles: proportional scaling
 * Edge handles: horizontal or vertical scaling
 */

import * as React from 'react';

export interface ResizeHandlesProps {
  /** Current uniform scale */
  scale: number;
  /** Current horizontal scale multiplier */
  scaleX: number;
  /** Current vertical scale multiplier */
  scaleY: number;
  /** Called when scale changes via handles */
  onScaleChange: (scaleX: number, scaleY: number) => void;
  /** Whether handles are visible */
  active: boolean;
}

type HandlePosition = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const HANDLE_SIZE = 10;

const HANDLE_CURSORS: Record<HandlePosition, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
};

/** Resize handles overlay */
export function ResizeHandles({
  scale,
  scaleX,
  scaleY,
  onScaleChange,
  active,
}: ResizeHandlesProps): React.ReactElement | null {
  const dragRef = React.useRef<{
    handle: HandlePosition;
    startX: number;
    startY: number;
    startScaleX: number;
    startScaleY: number;
  } | null>(null);

  const handleMouseDown = React.useCallback(
    (handle: HandlePosition, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      dragRef.current = {
        handle,
        startX: e.clientX,
        startY: e.clientY,
        startScaleX: scaleX,
        startScaleY: scaleY,
      };
    },
    [scaleX, scaleY],
  );

  const handleTouchStart = React.useCallback(
    (handle: HandlePosition, e: React.TouchEvent) => {
      e.stopPropagation();
      const touch = e.touches[0];
      dragRef.current = {
        handle,
        startX: touch.clientX,
        startY: touch.clientY,
        startScaleX: scaleX,
        startScaleY: scaleY,
      };
    },
    [scaleX, scaleY],
  );

  React.useEffect(() => {
    const handleMove = (clientX: number, clientY: number): void => {
      if (!dragRef.current) return;

      const { handle, startX, startY, startScaleX, startScaleY } = dragRef.current;
      const dx = clientX - startX;
      const dy = clientY - startY;

      // Sensitivity: pixels of movement per 0.1 scale change
      const sensitivity = 100;
      let newScaleX = startScaleX;
      let newScaleY = startScaleY;

      switch (handle) {
        // Corner handles: proportional
        case 'se': {
          const delta = (dx + dy) / 2;
          const factor = delta / sensitivity;
          newScaleX = startScaleX + factor;
          newScaleY = startScaleY + factor;
          break;
        }
        case 'nw': {
          const delta = (-dx + -dy) / 2;
          const factor = delta / sensitivity;
          newScaleX = startScaleX + factor;
          newScaleY = startScaleY + factor;
          break;
        }
        case 'ne': {
          const delta = (dx + -dy) / 2;
          const factor = delta / sensitivity;
          newScaleX = startScaleX + factor;
          newScaleY = startScaleY + factor;
          break;
        }
        case 'sw': {
          const delta = (-dx + dy) / 2;
          const factor = delta / sensitivity;
          newScaleX = startScaleX + factor;
          newScaleY = startScaleY + factor;
          break;
        }
        // Edge handles: single axis
        case 'e':
          newScaleX = startScaleX + dx / sensitivity;
          break;
        case 'w':
          newScaleX = startScaleX - dx / sensitivity;
          break;
        case 's':
          newScaleY = startScaleY + dy / sensitivity;
          break;
        case 'n':
          newScaleY = startScaleY - dy / sensitivity;
          break;
      }

      // Clamp to reasonable range
      newScaleX = Math.max(0.3, Math.min(3.0, newScaleX));
      newScaleY = Math.max(0.3, Math.min(3.0, newScaleY));

      onScaleChange(newScaleX, newScaleY);
    };

    const onMouseMove = (e: MouseEvent): void => {
      if (!dragRef.current) return;
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    };

    const onTouchMove = (e: TouchEvent): void => {
      if (!dragRef.current) return;
      e.preventDefault();
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    const onEnd = (): void => {
      dragRef.current = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [onScaleChange]);

  if (!active) return null;

  const handles: Array<{
    pos: HandlePosition;
    style: React.CSSProperties;
  }> = [
    // Corners
    { pos: 'nw', style: { top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2 } },
    { pos: 'ne', style: { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 } },
    { pos: 'se', style: { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 } },
    { pos: 'sw', style: { bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2 } },
    // Edges
    { pos: 'n', style: { top: -HANDLE_SIZE / 2, left: '50%', marginLeft: -HANDLE_SIZE / 2 } },
    { pos: 's', style: { bottom: -HANDLE_SIZE / 2, left: '50%', marginLeft: -HANDLE_SIZE / 2 } },
    { pos: 'w', style: { top: '50%', left: -HANDLE_SIZE / 2, marginTop: -HANDLE_SIZE / 2 } },
    { pos: 'e', style: { top: '50%', right: -HANDLE_SIZE / 2, marginTop: -HANDLE_SIZE / 2 } },
  ];

  // Scale display rounded
  const sxDisplay = Math.round(scaleX * scale * 100);
  const syDisplay = Math.round(scaleY * scale * 100);

  return (
    <>
      {/* Selection border */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          border: '2px solid rgba(59, 130, 246, 0.8)',
          borderRadius: 2,
        }}
      />

      {/* Handle dots */}
      {handles.map(({ pos, style }) => (
        <div
          key={pos}
          className="absolute z-50"
          style={{
            width: HANDLE_SIZE,
            height: HANDLE_SIZE,
            backgroundColor: '#fff',
            border: '2px solid rgba(59, 130, 246, 0.9)',
            borderRadius: pos.length === 2 ? 1 : '50%',
            cursor: HANDLE_CURSORS[pos],
            ...style,
          }}
          onMouseDown={(e) => handleMouseDown(pos, e)}
          onTouchStart={(e) => handleTouchStart(pos, e)}
        />
      ))}

      {/* Scale indicator */}
      {(scaleX !== 1 || scaleY !== 1) && (
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-blue-500 rounded text-[9px] text-white whitespace-nowrap pointer-events-none">
          {sxDisplay}% x {syDisplay}%
        </div>
      )}
    </>
  );
}
